import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Input,
  Select,
  Modal,
  Drawer,
  Space,
  Tooltip,
  Popconfirm,
  Tabs,
  Row,
  Col,
  Typography,
  message,
  Empty,
  Spin,
  Alert,
  Divider,
  Switch,
} from 'antd';
import {
  BugOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
  FilterOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  CodeOutlined,
  GlobalOutlined,
  UserOutlined,
  CalendarOutlined,
  ExperimentOutlined,
  ClockCircleOutlined,
  DesktopOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { appExceptionsApi } from '../../utils/api';
import { formatApiDateTimeJalali, formatDescriptiveDate } from '../../utils/dateUtils';

const { Text, Paragraph } = Typography;
const { Option } = Select;

// HTTP Status Code Badge Config
const getStatusCodeBadge = (statusCode) => {
  const code = Number(statusCode) || 500;
  if (code >= 500) {
    return {
      color: 'error',
      bgClass: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900',
      label: `${code} Server Error`,
    };
  }
  if (code === 404) {
    return {
      color: 'warning',
      bgClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
      label: '404 Not Found',
    };
  }
  if (code === 403) {
    return {
      color: 'orange',
      bgClass: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900',
      label: '403 Forbidden',
    };
  }
  if (code === 401) {
    return {
      color: 'gold',
      bgClass: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900',
      label: '401 Unauthorized',
    };
  }
  if (code === 422) {
    return {
      color: 'purple',
      bgClass: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900',
      label: '422 Unprocessable',
    };
  }
  return {
    color: 'default',
    bgClass: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    label: `${code}`,
  };
};

// HTTP Method Badge Config
const getMethodBadge = (method) => {
  const m = String(method || 'GET').toUpperCase();
  switch (m) {
    case 'GET':
      return { color: 'green', text: 'GET' };
    case 'POST':
      return { color: 'blue', text: 'POST' };
    case 'PUT':
      return { color: 'orange', text: 'PUT' };
    case 'PATCH':
      return { color: 'gold', text: 'PATCH' };
    case 'DELETE':
      return { color: 'red', text: 'DELETE' };
    case 'CLI':
      return { color: 'purple', text: 'CLI' };
    default:
      return { color: 'default', text: m };
  }
};

const Exceptions = () => {
  // Data states
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    server_5xx: 0,
    client_4xx: 0,
    recent_7_days: 0,
    top_exceptions: [],
  });
  const [meta, setMeta] = useState({
    available_classes: [],
    available_methods: [],
    available_paths: [],
  });

  // Filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, 24h, 7d, 30d

  // Detail Modal / Drawer state
  const [selectedException, setSelectedException] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');

  // Test Simulation Modal state
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testType, setTestType] = useState('runtime');
  const [testMessage, setTestMessage] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  // Clear all loading state
  const [clearLoading, setClearLoading] = useState(false);

  // Logging enable/disable setting state
  const [loggingEnabled, setLoggingEnabled] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Compute date range based on dateFilter preset
  const getDateRange = useCallback((preset) => {
    if (preset === 'all') return { date_from: null, date_to: null };
    const now = new Date();
    const toIso = now.toISOString();

    if (preset === 'today') {
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { date_from: startOfDay.toISOString(), date_to: toIso };
    }
    if (preset === '24h') {
      const past24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return { date_from: past24h.toISOString(), date_to: toIso };
    }
    if (preset === '7d') {
      const past7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { date_from: past7d.toISOString(), date_to: toIso };
    }
    if (preset === '30d') {
      const past30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { date_from: past30d.toISOString(), date_to: toIso };
    }
    return { date_from: null, date_to: null };
  }, []);

  // Fetch exceptions list
  const fetchExceptions = useCallback(
    async (page = 1, pageSize = 15) => {
      setLoading(true);
      try {
        const { date_from, date_to } = getDateRange(dateFilter);
        const params = {
          page,
          per_page: pageSize,
        };

        if (search.trim()) params.search = search.trim();
        if (statusFilter && statusFilter !== 'all') params.status_code = statusFilter;
        if (classFilter && classFilter !== 'all') params.exception_class = classFilter;
        if (methodFilter && methodFilter !== 'all') params.method = methodFilter;
        if (date_from) params.date_from = date_from;
        if (date_to) params.date_to = date_to;

        const res = await appExceptionsApi.list(params);
        const resData = res?.data || {};
        const paginator = resData.data && Array.isArray(resData.data.data) ? resData.data : resData;

        setExceptions(paginator.data || []);
        setPagination({
          current: paginator.current_page || page,
          pageSize: paginator.per_page || pageSize,
          total: paginator.total || 0,
        });

        if (resData.meta) {
          setMeta(resData.meta);
          if (resData.meta.logging_enabled !== undefined) {
            setLoggingEnabled(Boolean(resData.meta.logging_enabled));
          }
        }
      } catch (err) {
        message.error('خطا در دریافت لیست خطاها: ' + (err?.message || 'مشکل در برقراری ارتباط'));
      } finally {
        setLoading(false);
      }
    },
    [search, statusFilter, classFilter, methodFilter, dateFilter, getDateRange],
  );

  // Fetch stats overview
  const fetchStats = useCallback(async () => {
    try {
      const res = await appExceptionsApi.stats();
      if (res?.data) {
        setStats(res.data);
        if (res.data.logging_enabled !== undefined) {
          setLoggingEnabled(Boolean(res.data.logging_enabled));
        }
      }
    } catch (err) {
      // stats error handled silently
    }
  }, []);

  // Handler for toggle logging enabled/disabled
  const handleToggleLogging = async (checked) => {
    setToggleLoading(true);
    try {
      const res = await appExceptionsApi.updateSettings(checked);
      setLoggingEnabled(checked);
      message.success(
        res?.message || (checked ? 'ثبت خطاهای سیستم فعال شد' : 'ثبت خطاهای سیستم غیرفعال شد')
      );
      fetchStats();
    } catch (err) {
      message.error('خطا در تغییر وضعیت ثبت خطا: ' + (err?.message || 'ناموفق'));
    } finally {
      setToggleLoading(false);
    }
  };

  useEffect(() => {
    fetchExceptions(1, pagination.pageSize);
    fetchStats();
  }, [fetchExceptions, fetchStats, pagination.pageSize]);

  // Handler for reset filters
  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setClassFilter('all');
    setMethodFilter('all');
    setDateFilter('all');
  };

  // Handler for deleting single exception
  const handleDelete = async (id) => {
    try {
      await appExceptionsApi.delete(id);
      message.success('خطای مورد نظر حذف شد');
      fetchExceptions(pagination.current, pagination.pageSize);
      fetchStats();
      if (selectedException?.id === id) {
        setDetailModalOpen(false);
        setSelectedException(null);
      }
    } catch (err) {
      message.error('خطا در حذف رکورد: ' + (err?.message || 'ناموفق'));
    }
  };

  // Handler for clearing all exceptions
  const handleClearAll = async () => {
    setClearLoading(true);
    try {
      await appExceptionsApi.clearAll();
      message.success('تمام خطاهای سیستم با موفقیت پاک‌سازی شدند');
      fetchExceptions(1, pagination.pageSize);
      fetchStats();
      setDetailModalOpen(false);
      setSelectedException(null);
    } catch (err) {
      message.error('خطا در پاک‌سازی خطاها: ' + (err?.message || 'ناموفق'));
    } finally {
      setClearLoading(false);
    }
  };

  // Handler for triggering test exception simulation
  const handleTriggerTest = async () => {
    setTestLoading(true);
    try {
      const res = await appExceptionsApi.test({
        type: testType,
        message: testMessage || undefined,
      });
      message.success(res?.message || 'خطای آزمایشی با موفقیت در پایگاه داده ثبت شد');
      setTestModalOpen(false);
      setTestMessage('');
      fetchExceptions(1, pagination.pageSize);
      fetchStats();
    } catch (err) {
      message.error('خطا در شبیه‌سازی: ' + (err?.message || 'ناموفق'));
    } finally {
      setTestLoading(false);
    }
  };

  // Copy helper with feedback
  const handleCopy = (text, label = 'متن') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} در کلیپ‌بورد کپی شد`);
  };

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        title: 'وضعیت',
        dataIndex: 'status_code',
        key: 'status_code',
        width: 110,
        render: (status) => {
          const badge = getStatusCodeBadge(status);
          return (
            <Tag color={badge.color} className="font-mono text-xs font-semibold px-2 py-0.5 rounded-md">
              {status || 500}
            </Tag>
          );
        },
      },
      {
        title: 'متد',
        dataIndex: 'method',
        key: 'method',
        width: 80,
        render: (method) => {
          const badge = getMethodBadge(method);
          return (
            <Tag color={badge.color} className="font-mono text-[11px] font-bold px-1.5 py-0.5 rounded">
              {badge.text}
            </Tag>
          );
        },
      },
      {
        title: 'کلاس استثنا',
        dataIndex: 'exception_class',
        key: 'exception_class',
        width: 200,
        render: (cls) => {
          const shortName = cls ? cls.split('\\').pop() : 'Exception';
          return (
            <div className="flex flex-col" title={cls}>
              <span className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">
                {shortName}
              </span>
              <span className="text-[10px] text-gray-400 font-mono truncate max-w-[190px]">
                {cls}
              </span>
            </div>
          );
        },
      },
      {
        title: 'پیام خطا',
        dataIndex: 'message',
        key: 'message',
        render: (msg, record) => (
          <div className="flex flex-col max-w-md">
            <span
              className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2 cursor-pointer hover:text-indigo-600 transition"
              onClick={() => {
                setSelectedException(record);
                setDetailModalOpen(true);
              }}
              title={msg}
            >
              {msg || '(بدون پیام)'}
            </span>
            {record.file && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate mt-0.5">
                {record.file}:{record.line}
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'مسیر / اندپوینت',
        dataIndex: 'path',
        key: 'path',
        width: 200,
        render: (path, record) => (
          <div className="max-w-[190px] truncate" title={record.url || path}>
            <span className="font-mono text-[11px] text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.5 rounded border border-indigo-100 dark:border-indigo-900 truncate inline-block max-w-full">
              {path || record.url || '-'}
            </span>
          </div>
        ),
      },
      {
        title: 'کاربر / کلاینت',
        key: 'user',
        width: 140,
        render: (_, record) => {
          if (record.user_name || record.user_email) {
            return (
              <div className="flex flex-col text-xs">
                <span className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-[130px]">
                  {record.user_name || 'کاربر'}
                </span>
                <span className="text-[10px] text-gray-400 truncate max-w-[130px]">
                  {record.user_email}
                </span>
              </div>
            );
          }
          if (record.method === 'CLI') {
            return (
              <Tag color="purple" className="text-[10px] px-1.5 py-0 rounded">
                کنسول CLI
              </Tag>
            );
          }
          return (
            <span className="text-[11px] text-gray-400 font-mono">
              {record.ip || 'مهمان'}
            </span>
          );
        },
      },
      {
        title: 'زمان وقوع',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        render: (date) => (
          <div className="flex flex-col text-xs" title={date}>
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {formatApiDateTimeJalali(date)}
            </span>
            <span className="text-[10px] text-gray-400">
              {formatDescriptiveDate(date) || ''}
            </span>
          </div>
        ),
      },
      {
        title: 'عملیات',
        key: 'actions',
        width: 110,
        fixed: 'left',
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="مشاهده جزئیات کامل">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined className="text-indigo-600" />}
                onClick={() => {
                  setSelectedException(record);
                  setDetailModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="کپی پیام خطا">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined className="text-gray-500 hover:text-indigo-600" />}
                onClick={() => handleCopy(record.message, 'پیام خطا')}
              />
            </Tooltip>
            <Popconfirm
              title="آیا از حذف این خطا از پایگاه داده اطمینان دارید؟"
              okText="بله، حذف شود"
              cancelText="انصراف"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record.id)}
            >
              <Tooltip title="حذف">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [],
  );

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 space-y-6 max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-gray-200 dark:border-gray-800">
          <div>
            <div className="flex items-center gap-2 text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-1">
              <SafetyCertificateOutlined />
              <span>پنل اختصاصی سوپرادمین • لاگ‌های استثناهای لاراول</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
              <BugOutlined className="text-rose-600 dark:text-rose-400" />
              <span>گزارش خطاهای سیستم (Exceptions)</span>
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              تمام استثناها و خطاهای رخ‌داده در برنامه در دیتابیس ثبت شده و به صورت بلادرنگ از اینجا قابل ردیابی و جستجو هستند.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Logging Toggle Switch Card */}
            <div className="flex items-center gap-3 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
              <div className="flex flex-col text-right">
                <span className="text-xs font-bold text-gray-800 dark:text-gray-200 leading-tight">
                  ثبت خطاها (Logging)
                </span>
                <span
                  className={`text-[10px] font-medium ${
                    loggingEnabled
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-500 dark:text-rose-400'
                  }`}
                >
                  {loggingEnabled ? '● فعال' : '○ غیرفعال'}
                </span>
              </div>
              <Popconfirm
                title={
                  loggingEnabled
                    ? 'آیا مایل به متوقف کردن ثبت خطاهای سیستم هستید؟'
                    : 'آیا مایل به فعال‌سازی مجدد ثبت خطاهای سیستم هستید؟'
                }
                description={
                  loggingEnabled
                    ? 'با غیرفعال‌سازی، استثناهای جدید برنامه تا زمان فعال‌سازی مجدد در پایگاه داده ذخیره نخواهند شد.'
                    : 'با فعال‌سازی، کلیه استثناهای لاراول مجدداً در پایگاه داده ذخیره خواهند شد.'
                }
                okText={loggingEnabled ? 'بله، متوقف شود' : 'بله، فعال شود'}
                cancelText="انصراف"
                okButtonProps={{ danger: loggingEnabled }}
                onConfirm={() => handleToggleLogging(!loggingEnabled)}
              >
                <Switch
                  checked={loggingEnabled}
                  loading={toggleLoading}
                  checkedChildren="فعال"
                  unCheckedChildren="غیرفعال"
                  className={loggingEnabled ? 'bg-emerald-600' : 'bg-gray-400'}
                />
              </Popconfirm>
            </div>

            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchExceptions(pagination.current, pagination.pageSize);
                fetchStats();
              }}
              loading={loading}
              className="rounded-lg shadow-sm"
            >
              بروزرسانی
            </Button>

            <Button
              icon={<ExperimentOutlined />}
              onClick={() => setTestModalOpen(true)}
              className="rounded-lg border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 dark:border-indigo-800 dark:text-indigo-300 dark:bg-indigo-950/40 shadow-sm"
            >
              شبیه‌سازی خطای تستی
            </Button>

            <Popconfirm
              title="پاک‌سازی تمام لاگ‌های خطا"
              description="آیا مطمئن هستید که می‌خواهید تمام خطاهای ثبت‌شده در دیتابیس را برای همیشه حذف کنید؟"
              okText="بله، همه پاک شوند"
              cancelText="انصراف"
              okButtonProps={{ danger: true, loading: clearLoading }}
              onConfirm={handleClearAll}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={clearLoading}
                className="rounded-lg shadow-sm"
              >
                پاک‌سازی همه
              </Button>
            </Popconfirm>
          </div>
        </div>

        {/* Logging Disabled Warning Banner */}
        {!loggingEnabled && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined className="text-amber-600 text-lg" />}
            message={
              <span className="font-bold text-amber-900 dark:text-amber-200">
                ثبت خطاهای سیستم در حال حاضر غیرفعال است
              </span>
            }
            description="تنظیم لاگینگ غیرفعال است؛ بنابراین هیچ خطای جدیدی از سمت لاراول در پایگاه داده ذخیره نمی‌شود. شما می‌توانید برای رصد مجدد سیستم، با استفاده از دکمه زیر یا کلید سوئیچ بالا آن را فعال کنید."
            action={
              <Button
                size="small"
                type="primary"
                className="bg-amber-600 hover:bg-amber-700 border-none text-white rounded-lg font-medium shadow-sm"
                loading={toggleLoading}
                onClick={() => handleToggleLogging(true)}
              >
                فعال‌سازی مجدد ثبت خطاها
              </Button>
            }
            className="rounded-xl border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40"
          />
        )}

        {/* Stats Overview Metric Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} lg={4} className="w-full">
            <Card className="rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-800 hover:shadow transition">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">کل خطاهای ثبت‌شده</div>
              <div className="mt-2 text-2xl font-black text-gray-900 dark:text-white flex items-center justify-between">
                <span>{stats.total?.toLocaleString() ?? 0}</span>
                <span className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm">
                  <BugOutlined />
                </span>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={8} lg={5} className="w-full">
            <Card className="rounded-xl border border-rose-100 dark:border-rose-950 shadow-sm bg-white dark:bg-gray-800 hover:shadow transition">
              <div className="text-xs text-rose-600 dark:text-rose-400 font-medium">خطاهای امروز</div>
              <div className="mt-2 text-2xl font-black text-rose-600 dark:text-rose-400 flex items-center justify-between">
                <span>{stats.today?.toLocaleString() ?? 0}</span>
                <span className="p-2 rounded-lg bg-rose-50 dark:bg-rose-950/50 text-rose-600 text-sm">
                  <ClockCircleOutlined />
                </span>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={8} lg={5} className="w-full">
            <Card className="rounded-xl border border-red-100 dark:border-red-950 shadow-sm bg-white dark:bg-gray-800 hover:shadow transition">
              <div className="text-xs text-red-600 dark:text-red-400 font-medium">خطاهای سرور (5xx Server)</div>
              <div className="mt-2 text-2xl font-black text-red-600 dark:text-red-400 flex items-center justify-between">
                <span>{stats.server_5xx?.toLocaleString() ?? 0}</span>
                <span className="p-2 rounded-lg bg-red-50 dark:bg-red-950/50 text-red-600 text-sm">
                  <CloseCircleOutlined />
                </span>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={8} lg={5} className="w-full">
            <Card className="rounded-xl border border-amber-100 dark:border-amber-950 shadow-sm bg-white dark:bg-gray-800 hover:shadow transition">
              <div className="text-xs text-amber-600 dark:text-amber-400 font-medium">خطاهای کلاینت (4xx Client)</div>
              <div className="mt-2 text-2xl font-black text-amber-600 dark:text-amber-400 flex items-center justify-between">
                <span>{stats.client_4xx?.toLocaleString() ?? 0}</span>
                <span className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-600 text-sm">
                  <WarningOutlined />
                </span>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={8} lg={5} className="w-full">
            <Card className="rounded-xl border border-indigo-100 dark:border-indigo-950 shadow-sm bg-white dark:bg-gray-800 hover:shadow transition">
              <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">خطاهای ۷ روز اخیر</div>
              <div className="mt-2 text-2xl font-black text-indigo-600 dark:text-indigo-400 flex items-center justify-between">
                <span>{stats.recent_7_days?.toLocaleString() ?? 0}</span>
                <span className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 text-sm">
                  <CalendarOutlined />
                </span>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Search & Filter Toolbar */}
        <Card className="rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-800">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                <FilterOutlined className="text-indigo-600" />
                <span>جستجو و فیلترهای پیشرفته</span>
              </div>
              {(search || statusFilter !== 'all' || classFilter !== 'all' || methodFilter !== 'all' || dateFilter !== 'all') && (
                <Button
                  type="link"
                  size="small"
                  icon={<ClearOutlined />}
                  onClick={handleResetFilters}
                  className="text-xs text-rose-600 hover:text-rose-700 p-0"
                >
                  پاک کردن تمام فیلترها
                </Button>
              )}
            </div>

            <Row gutter={[12, 12]} align="middle">
              {/* Keyword Search */}
              <Col xs={24} md={8} lg={7}>
                <Input
                  prefix={<SearchOutlined className="text-gray-400 ml-1" />}
                  placeholder="جستجو در پیام، کلاس، مسیر، فایل، کاربر، IP یا کد..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onPressEnter={() => fetchExceptions(1, pagination.pageSize)}
                  allowClear
                  className="rounded-lg"
                />
              </Col>

              {/* Status Code Filter */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <Select
                  value={statusFilter}
                  onChange={(val) => setStatusFilter(val)}
                  className="w-full rounded-lg"
                  placeholder="کد وضعیت"
                >
                  <Option value="all">همه کدها</Option>
                  <Option value="5xx">خطای سرور (5xx)</Option>
                  <Option value="4xx">خطای کلاینت (4xx)</Option>
                  <Option value="500">500 Server Error</Option>
                  <Option value="404">404 Not Found</Option>
                  <Option value="403">403 Forbidden</Option>
                  <Option value="401">401 Unauthorized</Option>
                  <Option value="422">422 Validation</Option>
                </Select>
              </Col>

              {/* HTTP Method Filter */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <Select
                  value={methodFilter}
                  onChange={(val) => setMethodFilter(val)}
                  className="w-full rounded-lg"
                  placeholder="متد درخواست"
                >
                  <Option value="all">همه متدها</Option>
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="DELETE">DELETE</Option>
                  <Option value="CLI">CLI (کنسول)</Option>
                </Select>
              </Col>

              {/* Exception Class Filter */}
              <Col xs={12} sm={6} md={4} lg={5}>
                <Select
                  value={classFilter}
                  onChange={(val) => setClassFilter(val)}
                  className="w-full rounded-lg"
                  placeholder="نوع استثنا (Class)"
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">همه کلاس‌های خطا</Option>
                  {meta.available_classes?.map((cls) => (
                    <Option key={cls} value={cls}>
                      {cls.split('\\').pop()}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Date Filter Preset */}
              <Col xs={12} sm={6} md={4} lg={4}>
                <Select
                  value={dateFilter}
                  onChange={(val) => setDateFilter(val)}
                  className="w-full rounded-lg"
                  placeholder="بازه زمانی"
                >
                  <Option value="all">همه زمان‌ها</Option>
                  <Option value="today">امروز</Option>
                  <Option value="24h">۲۴ ساعت گذشته</Option>
                  <Option value="7d">۷ روز اخیر</Option>
                  <Option value="30d">۳۰ روز اخیر</Option>
                </Select>
              </Col>

              {/* Apply Search Button */}
              <Col xs={24} md={4} lg={2} className="flex justify-end">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => fetchExceptions(1, pagination.pageSize)}
                  loading={loading}
                  className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 shadow-sm"
                >
                  اعمال
                </Button>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Exceptions Table Card */}
        <Card
          className="rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-800 overflow-hidden"
          bodyStyle={{ padding: 0 }}
        >
          <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-800 dark:text-gray-200 text-sm">لیست خطاها</span>
              <Tag color="indigo" className="font-mono text-xs rounded-full">
                {pagination.total} رکورد
              </Tag>
            </div>
            <div className="text-xs text-gray-400">
              برای مشاهده اطلاعات کامل و استک‌ترس، بر روی هر سطر کلیک کنید.
            </div>
          </div>

          <Table
            dataSource={exceptions}
            columns={columns}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '25', '50', '100'],
              showTotal: (total, range) => `نمایش ${range[0]} تا ${range[1]} از کل ${total} خطا`,
              onChange: (page, pageSize) => fetchExceptions(page, pageSize),
            }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedException(record);
                setDetailModalOpen(true);
              },
              className: 'cursor-pointer hover:bg-gray-50/70 dark:hover:bg-gray-800/60 transition',
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="text-gray-400 text-sm">
                      هیچ خطایی مطابق با فیلترهای انتخابی یافت نشد 🎉
                    </span>
                  }
                />
              ),
            }}
          />
        </Card>

        {/* Detail Modal / Drawer */}
        <Modal
          title={
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-950/50">
                <BugOutlined className="text-lg" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-base text-gray-900 dark:text-white">
                  جزئیات خطای لاراول #{selectedException?.id}
                </span>
                <span className="text-xs text-gray-400 font-mono">
                  {selectedException?.exception_class}
                </span>
              </div>
            </div>
          }
          open={detailModalOpen}
          onCancel={() => setDetailModalOpen(false)}
          width={1000}
          footer={[
            <Button
              key="copy"
              icon={<CopyOutlined />}
              onClick={() => {
                const report = `[Laravel Exception #${selectedException?.id}]
Class: ${selectedException?.exception_class}
Status: ${selectedException?.status_code}
Message: ${selectedException?.message}
File: ${selectedException?.file}:${selectedException?.line}
URL: ${selectedException?.method} ${selectedException?.url || selectedException?.path}
User: ${selectedException?.user_name || 'Guest'} (${selectedException?.user_email || 'N/A'})
Time: ${selectedException?.created_at}

Stack Trace:
${selectedException?.trace}`;
                handleCopy(report, 'گزارش کامل خطا');
              }}
            >
              کپی گزارش کامل
            </Button>,
            <Popconfirm
              key="delete"
              title="آیا از حذف این لاگ خطا مطمئن هستید؟"
              okText="بله، حذف"
              cancelText="انصراف"
              okButtonProps={{ danger: true }}
              onConfirm={() => selectedException && handleDelete(selectedException.id)}
            >
              <Button danger icon={<DeleteOutlined />}>
                حذف این خطا
              </Button>
            </Popconfirm>,
            <Button key="close" type="primary" onClick={() => setDetailModalOpen(false)}>
              بستن
            </Button>,
          ]}
        >
          {selectedException && (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              {/* Status Header Badge Banner */}
              <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/50 dark:border-rose-900 dark:bg-rose-950/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Tag
                    color={getStatusCodeBadge(selectedException.status_code).color}
                    className="font-mono text-sm px-2.5 py-1 font-bold rounded-lg"
                  >
                    HTTP {selectedException.status_code || 500}
                  </Tag>
                  <Tag
                    color={getMethodBadge(selectedException.method).color}
                    className="font-mono text-sm px-2.5 py-1 font-bold rounded-lg"
                  >
                    {selectedException.method}
                  </Tag>
                  <span className="font-mono text-xs text-gray-600 dark:text-gray-300 font-bold truncate max-w-sm sm:max-w-md">
                    {selectedException.path || selectedException.url || '-'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {formatApiDateTimeJalali(selectedException.created_at)}
                </div>
              </div>

              {/* Exception Message Box */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    پیام استثنا (Message):
                  </span>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(selectedException.message, 'پیام خطا')}
                    className="text-xs p-0 text-indigo-600"
                  >
                    کپی پیام
                  </Button>
                </div>
                <div className="font-mono text-xs text-rose-700 dark:text-rose-300 bg-white dark:bg-black/40 p-3 rounded-lg border border-rose-100 dark:border-rose-950/60 break-words whitespace-pre-wrap select-all">
                  {selectedException.message || '(بدون پیام استثنا)'}
                </div>
              </div>

              {/* Meta Grid Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <span className="text-gray-400 block mb-1">فایل و شماره خط:</span>
                  <div className="font-mono text-gray-800 dark:text-gray-200 break-all select-all flex items-center justify-between">
                    <span>
                      {selectedException.file || 'N/A'}:{selectedException.line || '0'}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopy(`${selectedException.file}:${selectedException.line}`, 'مسیر فایل')}
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <span className="text-gray-400 block mb-1">آدرس کامل درخواست (URL):</span>
                  <div className="font-mono text-gray-800 dark:text-gray-200 break-all select-all flex items-center justify-between">
                    <span className="truncate">{selectedException.url || selectedException.path || 'CLI'}</span>
                    {selectedException.url && (
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(selectedException.url, 'URL')}
                      />
                    )}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <span className="text-gray-400 block mb-1">کاربر جاری:</span>
                  <div className="text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <UserOutlined className="text-indigo-600" />
                    <span>
                      {selectedException.user_name || 'کاربر احراز نشده / مهمان'}
                      {selectedException.user_email ? ` (${selectedException.user_email})` : ''}
                      {selectedException.user_id ? ` [شناسه: ${selectedException.user_id}]` : ''}
                    </span>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <span className="text-gray-400 block mb-1">آدرس IP و شناسه شرکت:</span>
                  <div className="font-mono text-gray-800 dark:text-gray-200 flex items-center gap-3">
                    <span className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-[11px]">
                      IP: {selectedException.ip || 'Local / CLI'}
                    </span>
                    {selectedException.company_id && (
                      <span className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-[11px]">
                        Company ID: {selectedException.company_id}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs for Trace, Request Payload, Headers */}
              <Tabs
                activeKey={activeDetailTab}
                onChange={setActiveDetailTab}
                items={[
                  {
                    key: 'trace',
                    label: (
                      <span className="flex items-center gap-1.5 font-medium">
                        <CodeOutlined />
                        <span>ردیابی استک (Stack Trace)</span>
                      </span>
                    ),
                    children: (
                      <div className="relative">
                        <div className="flex justify-end mb-2">
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopy(selectedException.trace, 'استک‌پشته')}
                          >
                            کپی استک‌پشته
                          </Button>
                        </div>
                        <pre className="font-mono text-[11px] bg-gray-900 text-gray-100 p-4 rounded-xl max-h-[350px] overflow-auto border border-gray-800 leading-relaxed select-all" dir="ltr">
                          {selectedException.trace || '(هیچ استک‌پشته‌ای موجود نیست)'}
                        </pre>
                      </div>
                    ),
                  },
                  {
                    key: 'payload',
                    label: (
                      <span className="flex items-center gap-1.5 font-medium">
                        <FileTextOutlined />
                        <span>داده‌های ارسالی (Payload)</span>
                      </span>
                    ),
                    children: (
                      <div className="relative">
                        <div className="flex justify-end mb-2">
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() =>
                              handleCopy(
                                JSON.stringify(selectedException.request_payload, null, 2),
                                'داده‌های درخواست',
                              )
                            }
                          >
                            کپی داده‌ها
                          </Button>
                        </div>
                        <pre className="font-mono text-[11px] bg-gray-900 text-emerald-400 p-4 rounded-xl max-h-[350px] overflow-auto border border-gray-800 leading-relaxed select-all" dir="ltr">
                          {selectedException.request_payload && Object.keys(selectedException.request_payload).length > 0
                            ? JSON.stringify(selectedException.request_payload, null, 2)
                            : '// هیچ پارامتر یا داده ارسالی ثبت نشده است (یا درخواست خالی بوده است)'}
                        </pre>
                      </div>
                    ),
                  },
                  {
                    key: 'headers',
                    label: (
                      <span className="flex items-center gap-1.5 font-medium">
                        <DesktopOutlined />
                        <span>سربرگ‌های درخواست (Headers)</span>
                      </span>
                    ),
                    children: (
                      <div className="relative">
                        <div className="flex justify-end mb-2">
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() =>
                              handleCopy(
                                JSON.stringify(selectedException.headers, null, 2),
                                'سربرگ‌ها',
                              )
                            }
                          >
                            کپی سربرگ‌ها
                          </Button>
                        </div>
                        <pre className="font-mono text-[11px] bg-gray-900 text-cyan-300 p-4 rounded-xl max-h-[350px] overflow-auto border border-gray-800 leading-relaxed select-all" dir="ltr">
                          {selectedException.headers && Object.keys(selectedException.headers).length > 0
                            ? JSON.stringify(selectedException.headers, null, 2)
                            : '// هیچ سربرگی موجود نیست'}
                        </pre>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Modal>

        {/* Test Simulation Modal */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <ExperimentOutlined className="text-indigo-600" />
              <span>شبیه‌سازی خطای آزمایشی لاراول</span>
            </div>
          }
          open={testModalOpen}
          onCancel={() => setTestModalOpen(false)}
          onOk={handleTriggerTest}
          confirmLoading={testLoading}
          okText="شبیه‌سازی و ثبت در دیتابیس"
          cancelText="انصراف"
        >
          <div className="space-y-4 py-2">
            <Alert
              type="info"
              showIcon
              message="با اجرای این قابلیت، یک استثنا به صورت عمدی توسط بک‌اند پرتاب شده و در پایگاه داده ثبت می‌شود تا صحت عملکرد سیستم لاگینگ آزمایش گردد."
            />

            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1.5">
                نوع خطای آزمایشی:
              </label>
              <Select
                value={testType}
                onChange={setTestType}
                className="w-full rounded-lg"
              >
                <Option value="runtime">RuntimeException (خطای عمومی حین اجرا)</Option>
                <Option value="query">QueryException (خطای دیتابیس و SQL Query)</Option>
                <Option value="model_not_found">ModelNotFoundException (یافت نشدن رکورد - 404)</Option>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1.5">
                پیام سفارشی (اختیاری):
              </label>
              <Input
                placeholder="مثلاً: خطای آزمایشی ارزیابی اتصال به دیتابیس..."
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="rounded-lg"
              />
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Exceptions;
